import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { Button } from '@strata/react-buttons';
import { ComparisonColumns } from '@strata/react-comparison-columns';
import { EnrollmentCheckDate } from '@strata/react-enrollment-dates';
import { FormTitleLabel } from '@strata/react-label-value-pairs';
import { MessagingBlock } from '@strata/react-messaging-block';
import PropTypes from 'prop-types';

import { buildEnrollmentHistoryColumns } from 'components/enrollment/EnrollmentHistoryColumns';
import { useGetEnrollmentChangeHistoryQuery } from 'store/api/scopeAssessorApi';

const normalizeEnrollmentChangeHistory = (data, isError) => data ?? (isError ? [] : undefined);

export const EnrollmentHistory = ({
    payerId = undefined,
    memberId = undefined,
    firstName = undefined,
    lastName = undefined,
    dateOfBirth = undefined,
    memberDetails = undefined,
    onRunEnrollmentCheck = undefined,
    // eslint-disable-next-line complexity
}) => {
    const canFetch = payerId && memberId && firstName && lastName && dateOfBirth;
    const memberParams = canFetch
        ? { payerId, memberId, firstName, lastName, dateOfBirth }
        : skipToken;

    const {
        data: enrollmentChangeHistoryData,
        isFetching,
        isError,
    } = useGetEnrollmentChangeHistoryQuery(memberParams);
    const enrollmentChangeHistory = normalizeEnrollmentChangeHistory(
        enrollmentChangeHistoryData,
        isError,
    );

    if (!canFetch || isFetching || enrollmentChangeHistory === undefined) {
        return null;
    }
    const { rows, columns } = buildEnrollmentHistoryColumns(enrollmentChangeHistory);
    const noEnrollmentChanges = !enrollmentChangeHistory.length;

    return (
        <>
            <div className="sds_enrollment-comparison__header">
                <FormTitleLabel label="ENROLLMENT CHANGE HISTORY" />
                <div className="sds_enrollment-comparison__header-actions">
                    {memberDetails?.enrollmentCheckDate && (
                        <span>
                            Latest enrollment check:{' '}
                            <EnrollmentCheckDate
                                enrollmentCheckDate={memberDetails.enrollmentCheckDate}
                            />
                        </span>
                    )}
                    {memberDetails?.isApiBenefitCheckEnabled && (
                        <Button
                            intent={Button.Intent.PRIMARY}
                            label="Run Enrollment Check"
                            onClick={onRunEnrollmentCheck}
                        />
                    )}
                </div>
            </div>
            {noEnrollmentChanges ? (
                <MessagingBlock
                    size={MessagingBlock.Sizes.SMALL}
                    icon={IconNames.CLIPBOARD}
                    primaryText="No enrollment changes."
                />
            ) : (
                <ComparisonColumns rows={rows} columns={columns} updatedTooltip="Changed" />
            )}
        </>
    );
};

EnrollmentHistory.propTypes = {
    payerId: PropTypes.string,
    memberId: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    dateOfBirth: PropTypes.string,
    memberDetails: PropTypes.shape({
        enrollmentCheckDate: PropTypes.string,
    }),
    onRunEnrollmentCheck: PropTypes.func,
};
